import { useEffect, useState } from "react";
import { Button, Card, Group, Paper, Select, Stack, Text, TextInput, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { DriveSpreadsheet } from "../../api/types";

export function ShipmentsSettingsTab() {
  const { t } = useTranslation();
  const [codeFormat, setCodeFormat] = useState("GM-LW-999");
  const [busy, setBusy] = useState(false);

  const [spreadsheets, setSpreadsheets] = useState<DriveSpreadsheet[]>([]);
  const [docId, setDocId] = useState<string | null>(null);
  const [docName, setDocName] = useState("");
  const [docIdDefect, setDocIdDefect] = useState<string | null>(null);
  const [docNameDefect, setDocNameDefect] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.getSetting("shipment.code_format");
        if (typeof r.value === "string" && r.value) setCodeFormat(r.value);
      } catch { /* ignore */ }
    })();
    // NU încărcăm lista de spreadsheet-uri la montare (necesită Drive conectat și
    // ar arunca un 503 la fiecare deschidere a paginii Settings). Se încarcă la
    // apăsarea pe „Reîmprospătează". Doar citim setarea curentă.
    void api.getSetting("sheets.export_spreadsheet_id").then((r) => setDocId((r.value as string) || null)).catch(() => undefined);
    void api.getSetting("sheets.export_spreadsheet_name").then((r) => setDocName((r.value as string) || "")).catch(() => undefined);
    void api.getSetting("sheets.defect_spreadsheet_id").then((r) => setDocIdDefect((r.value as string) || null)).catch(() => undefined);
    void api.getSetting("sheets.defect_spreadsheet_name").then((r) => setDocNameDefect((r.value as string) || "")).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function save() {
    setBusy(true);
    try {
      await api.putSetting("shipment.code_format", codeFormat.trim() || "GM-LW-999");
      notifications.show({ color: "green", message: t("settings.shipmentSettingsSaved") });
    } catch (err) {
      notifications.show({ color: "red", message: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  async function loadSpreadsheets() {
    try {
      setSpreadsheets(await api.listDriveSpreadsheets());
    } catch {
      // Drive neconectat/neautentificat (503) sau altă eroare — mesaj prietenos.
      notifications.show({ color: "red", message: t("settings.sheetsExport.loadError") });
    }
  }

  async function saveDoc() {
    if (!docId) return;
    try {
      await api.putSetting("sheets.export_spreadsheet_id", docId);
      await api.putSetting("sheets.export_spreadsheet_name", docName);
      notifications.show({ color: "green", message: t("settings.sheetsExport.saved") });
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  async function saveDocDefect() {
    if (!docIdDefect) return;
    try {
      await api.putSetting("sheets.defect_spreadsheet_id", docIdDefect);
      await api.putSetting("sheets.defect_spreadsheet_name", docNameDefect);
      notifications.show({ color: "green", message: t("settings.sheetsExport.saved") });
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  return (
    <Stack gap="md">
      <Paper withBorder p="md">
        <Stack gap="sm">
          <TextInput
            label={t("settings.shipmentCodeFormat")}
            description={t("settings.shipmentCodeFormatHint")}
            value={codeFormat}
            onChange={(e) => setCodeFormat(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button onClick={() => void save()} loading={busy}>
              {t("common.save")}
            </Button>
          </Group>
        </Stack>
      </Paper>

      <Card withBorder mt="lg">
        <Title order={5} mb={4}>{t("settings.sheetsExport.title")}</Title>
        <Text size="xs" c="dimmed" mb="sm">{t("settings.sheetsExport.hint")}</Text>
        <Stack gap="xs">
          <Select
            label={t("settings.sheetsExport.docLabel")}
            data={spreadsheets.map((s) => ({ value: s.id, label: s.name }))}
            value={docId}
            searchable
            nothingFoundMessage={t("settings.sheetsExport.none")}
            onChange={(v) => {
              setDocId(v);
              setDocName(spreadsheets.find((s) => s.id === v)?.name ?? "");
            }}
          />
          <Group>
            <Button variant="default" size="xs" onClick={() => void loadSpreadsheets()}>
              {t("settings.sheetsExport.refresh")}
            </Button>
            <Button size="xs" disabled={!docId} onClick={() => void saveDoc()}>
              {t("settings.sheetsExport.save")}
            </Button>
          </Group>
          <Text size="xs" c="dimmed">
            {docName ? t("settings.sheetsExport.current", { name: docName }) : t("settings.sheetsExport.none")}
          </Text>
          <Select
            label={t("settings.sheetsExport.docLabelDefect")}
            data={spreadsheets.map((s) => ({ value: s.id, label: s.name }))}
            value={docIdDefect}
            searchable
            nothingFoundMessage={t("settings.sheetsExport.none")}
            onChange={(v) => {
              setDocIdDefect(v);
              setDocNameDefect(spreadsheets.find((s) => s.id === v)?.name ?? "");
            }}
          />
          <Group>
            <Button size="xs" disabled={!docIdDefect} onClick={() => void saveDocDefect()}>
              {t("settings.sheetsExport.save")}
            </Button>
          </Group>
          <Text size="xs" c="dimmed">
            {docNameDefect ? t("settings.sheetsExport.currentDefect", { name: docNameDefect }) : t("settings.sheetsExport.none")}
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
}
